<template>
  <div>
    <div :class="$style.modalContext">
      {{ $t('components.bankInfo.title') }}
    </div>
    <MCAVendorFieldset>
      <!-- Bank Name-->
      <UiInput
        ref="vendorBankNameInput"
        name="bankName"
        type="text"
        :label="$t('common.bankInfo.bankName')"
        :value="bank.bankName"
        @input="setVendorProps"
      />
      <!-- Recipient Bank Account Name-->
      <UiInput
        ref="vendorNameOnAccountInput"
        name="nameOnAccount"
        type="text"
        :label="$t('components.bankInfo.recipientBankName')"
        :error-msg="$t('common.errors.mandatory')"
        :value="bank.nameOnAccount"
        required
        @input="setVendorProps"
      />
    </MCAVendorFieldset>
    <MCAVendorFieldset>
      <!-- Bank Account Number -->
      <UiInput
        v-if="requiredVendorFields.includes('acctNumber')"
        ref="vendorAccountNumberInput"
        name="accountNumber"
        type="text"
        :label="$t('common.bankInfo.bankNumber')"
        :value="bank.accountNumber"
        required
        :error="regexValidator(bank.accountNumber, 'acctNumber')"
        :error-msg="
          validationMsg(
            bank.accountNumber,
            $t('common.bankInfo.bankNumberError'),
          )
        "
        @input="setVendorProps"
      />
      <!-- BIC/SWIFT Code-->
      <UiInput
        v-if="requiredVendorFields.includes('bicSwift')"
        ref="vendorSwiftCodeInput"
        name="swiftCode"
        type="text"
        :label="$t('common.bankInfo.swiftCode')"
        :value="bank.swiftCode"
        :error="
          regexValidator(bank.swiftCode, 'bicSwift') &&
          currencyCloudDependantRequirements('bicSwift')
        "
        :error-msg="
          validationMsg(bank.swiftCode, $t('common.bankInfo.swiftCodeError'))
        "
        :required="currencyCloudDependantRequirements('bicSwift')"
        @input="setVendorProps"
      />
      <!-- IBAN -->
      <UiInput
        v-if="requiredVendorFields.includes('iban')"
        ref="vendorIbanInput"
        name="iban"
        type="text"
        :label="$t('common.bankInfo.iban')"
        :value="bank.iban"
        :error="
          regexValidator(bank.iban, 'iban') &&
          currencyCloudDependantRequirements('iban')
        "
        :error-msg="validationMsg(bank.iban, $t('common.bankInfo.ibanError'))"
        :required="currencyCloudDependantRequirements('iban')"
        @input="setVendorProps"
      />
      <!-- Sort Code -->
      <UiInput
        v-if="requiredVendorFields.includes('sortCode')"
        ref="vendorSortCodeInput"
        name="sortCode"
        type="text"
        :label="$t('common.bankInfo.sortCode')"
        :value="bank.sortCode"
        :error="
          regexValidator(bank.sortCode, 'sortCode') &&
          currencyCloudDependantRequirements('sortCode')
        "
        :error-msg="
          validationMsg(bank.sortCode, $t('common.bankInfo.sortCodeError'))
        "
        :required="currencyCloudDependantRequirements('sortCode')"
        @input="setVendorProps"
      />
      <!-- Routing Number -->
      <UiInput
        v-if="requiredVendorFields.includes('aba')"
        ref="vendorRoutingNumberInput"
        name="routingNumber"
        type="text"
        :label="$t('common.bankInfo.ach')"
        :value="bank.routingNumber"
        :error="regexValidator(bank.routingNumber, 'aba')"
        :error-msg="
          validationMsg(bank.routingNumber, $t('common.bankInfo.achError'))
        "
        required
        @input="setVendorProps"
      />
      <!-- Wire Routing Number -->
      <UiInput
        v-if="requiredVendorFields.includes('aba')"
        ref="vendorWireRoutingNumberInput"
        name="wireRoutingNumber"
        type="text"
        :label="$t('common.bankInfo.wireNumber')"
        :value="bank.wireRoutingNumber"
        :error="
          regexValidator(bank.wireRoutingNumber, 'aba') &&
          currencyCloudDependantRequirements('aba')
        "
        :error-msg="
          validationMsg(
            bank.wireRoutingNumber,
            $t('common.bankInfo.wireNumberError'),
          )
        "
        :required="currencyCloudDependantRequirements('aba')"
        @input="setVendorProps"
      />
      <!-- BSB Code -->
      <UiInput
        v-if="requiredVendorFields.includes('bsbCode')"
        ref="vendorBsbCodeInput"
        name="bsbCode"
        type="text"
        :label="$t('common.bankInfo.bsbCode')"
        :value="bank.bsbCode"
        :error="regexValidator(bank.bsbCode, 'bsbCode')"
        :error-msg="
          validationMsg(bank.bsbCode, $t('common.bankInfo.bsbCodeError'))
        "
        required
        @input="setVendorProps"
      />
      <!-- Bank Code -->
      <UiInput
        v-if="requiredVendorFields.includes('bankCode')"
        ref="vendorBankCodeInput"
        name="bankCode"
        type="text"
        :label="$t('common.bankInfo.bankCode')"
        :value="bank.bankCode"
        :error="regexValidator(bank.bankCode, 'bankCode')"
        :error-msg="
          validationMsg(bank.bankCode, $t('common.bankInfo.bankCodeError'))
        "
        required
        @input="setVendorProps"
      />
      <!-- Branch Code -->
      <UiInput
        v-if="requiredVendorFields.includes('branchCode')"
        ref="vendorBranchCodeInput"
        name="branchCode"
        type="text"
        :label="$t('common.bankInfo.branchCode')"
        :value="bank.branchCode"
        :error="regexValidator(bank.branchCode, 'branchCode')"
        :error-msg="
          validationMsg(bank.branchCode, $t('common.bankInfo.branchCodeError'))
        "
        required
        @input="setVendorProps"
      />
      <!-- CLABE -->
      <UiInput
        v-if="requiredVendorFields.includes('clabe')"
        ref="vendorClabeInput"
        name="clabe"
        type="text"
        :label="$t('common.bankInfo.clabe')"
        :value="bank.clabe"
        :error="regexValidator(bank.clabe, 'clabe')"
        :error-msg="validationMsg(bank.clabe, $t('common.bankInfo.clabeError'))"
        required
        @input="setVendorProps"
      />
      <!-- CNAPS -->
      <UiInput
        v-if="requiredVendorFields.includes('cnaps')"
        ref="vendorCnapsInput"
        name="cnaps"
        type="text"
        :label="$t('common.bankInfo.cnaps')"
        :value="bank.cnaps"
        :error="regexValidator(bank.cnaps, 'cnaps')"
        :error-msg="validationMsg(bank.cnaps, $t('common.bankInfo.cnapsError'))"
        required
        @input="setVendorProps"
      />
      <!-- IFSC -->
      <UiInput
        v-if="requiredVendorFields.includes('ifsc')"
        ref="vendorIfscInput"
        name="ifsc"
        type="text"
        :label="$t('common.bankInfo.ifsc')"
        :value="bank.ifsc"
        :error="regexValidator(bank.ifsc, 'ifsc')"
        :error-msg="validationMsg(bank.ifsc, $t('common.bankInfo.ifscError'))"
        required
        @input="setVendorProps"
      />
    </MCAVendorFieldset>
    <div :class="$style.modalContext">
      {{ $t('components.bankInfo.recipientBankAddress') }}
    </div>
    <MCAVendorFieldset>
      <!-- Bank Address Line 1 -->
      <UiInput
        ref="vendorLine1Input"
        name="line1"
        type="text"
        :error-msg="$t('common.errors.mandatory')"
        :label="$t('common.address.addressLine1')"
        :value="bank.line1"
        :required="requiredVendorFields.includes('bankAddress')"
        @input="setVendorProps"
      />
      <!-- Bank Address Line 2 -->
      <UiInput
        ref="vendorLine2Input"
        name="line2"
        type="text"
        :error-msg="$t('common.errors.mandatory')"
        :label="$t('common.address.addressLine2')"
        :value="bank.line2"
        :required="requiredVendorFields.includes('bankAddress')"
        @input="setVendorProps"
      />
      <!-- Bank Address Country -->
      <UiInput
        name="country"
        type="text"
        :error-msg="$t('common.errors.mandatory')"
        :label="$t('common.address.country')"
        :value="getCountryName(selectedVendor.bankAddress.country)"
        :required="requiredVendorFields.includes('bankAddress')"
        disabled
        @input="setVendorProps"
      />
      <!-- Bank Address State -->
      <UiDropdown
        v-if="stateOptionsForCountry(selectedVendor.bankAddress.country)"
        ref="vendorStateInput"
        size="small"
        name="state"
        :external-label="stateLabel(selectedVendor.bankAddress.country)"
        :label="
          $t('common.selectOption', {
            option: `${stateLabel(selectedVendor.bankAddress.country)} -`,
          })
        "
        :options="stateOptionsForCountry(selectedVendor.bankAddress.country)"
        :value="bank.state"
        :required="requiredVendorFields.includes('bankAddress')"
        @input="setVendorProps"
      />
      <!-- Bank Address State for countries without a list of states -->
      <UiInput
        v-else
        ref="vendorStateInput"
        name="state"
        :label="$t('common.address.state')"
        :value="bank.state"
        :required="requiredVendorFields.includes('bankAddress')"
        @input="setVendorProps"
      />
      <!-- Bank Address City -->
      <UiInput
        ref="vendorCityInput"
        name="city"
        type="text"
        :label="$t('common.address.city')"
        :value="bank.city"
        :required="requiredVendorFields.includes('bankAddress')"
        @input="setVendorProps"
      />
      <!-- Bank Address Postal Code -->
      <UiInput
        v-if="
          bank.country && !withoutPostalCodeCountries.includes(bank.country)
        "
        ref="vendorPostalCodeInput"
        name="postalCode"
        type="text"
        :label="$t('common.address.postalCodeLabel.postalCode')"
        :value="bank.postalCode"
        :required="requiredVendorFields.includes('bankAddress')"
        @input="setVendorProps"
      />
    </MCAVendorFieldset>
    <UiButton :text="$t('common.buttons.back')" @click="handlePrev" />
    <UiButton
      :text="$t('common.buttons.next')"
      data-cy="next-button"
      @click="handleNext"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { UiDropdown, UiButton, UiInput } from 'ui-components';
import {
  COUNTRY_CODE_WITH_NAMES,
  WITHOUT_POSTAL_CODE_COUNTRY_CODE,
} from '@/data/country-code-with-names';
import { stateOptionsForCountry, stateLabel } from '@/utils/local';
import MCAVendorFieldset from './MCAVendorFieldset.vue';
export default {
  components: {
    UiButton,
    UiInput,
    UiDropdown,
    MCAVendorFieldset,
  },
  props: {
    requiredVendorFieldsWithValidation: { type: Object, required: true },
  },
  data() {
    return {
      bank: {},
    };
  },
  mounted() {
    if (!this.selectedVendor) {
      return;
    }
    this.bank = {
      bankName: this.selectedVendor.bankName,
      nameOnAccount: this.selectedVendor.nameOnAccount,
      accountNumber: this.selectedVendor.accountNumber,
      swiftCode: this.selectedVendor.swiftCode,
      iban: this.selectedVendor.iban,
      sortCode: this.selectedVendor.sortCode,
      routingNumber: this.selectedVendor.routingNumber,
      wireRoutingNumber: this.selectedVendor.wireRoutingNumber,
      bsbCode: this.selectedVendor.bsbCode,
      bankCode: this.selectedVendor.bankCode,
      branchCode: this.selectedVendor.branchCode,
      clabe: this.selectedVendor.clabe,
      cnaps: this.selectedVendor.cnaps,
      ifsc: this.selectedVendor.ifsc,
      line1: this.selectedVendor.bankAddress.line1,
      line2: this.selectedVendor.bankAddress.line2,
      country: this.selectedVendor.bankAddress.country,
      state: this.selectedVendor.bankAddress.state,
      city: this.selectedVendor.bankAddress.city,
      postalCode: this.selectedVendor.bankAddress.postalCode,
    };
  },
  computed: {
    ...mapGetters(['selectedVendor']),
    validationRegex() {
      return Object.keys(this.requiredVendorFieldsWithValidation).reduce(
        (acc, key) => {
          acc[key] = RegExp(this.requiredVendorFieldsWithValidation[key]);
          return acc;
        },
        {},
      );
    },
    requiredVendorFields() {
      return Object.keys(this.requiredVendorFieldsWithValidation);
    },
    withoutPostalCodeCountries() {
      return WITHOUT_POSTAL_CODE_COUNTRY_CODE;
    },
  },
  methods: {
    stateOptionsForCountry,
    stateLabel,
    getCountryName(countryCode) {
      return COUNTRY_CODE_WITH_NAMES[countryCode]();
    },
    setVendorProps(val, name) {
      if (name) {
        this.bank[name] = val;
      }
    },
    // Returns the correct custom message in case of error
    validationMsg(propVal, customMsg) {
      return !propVal ? this.$t('common.errors.mandatory') : customMsg;
    },
    // Does validation on given field based on currency cloud regex requirements
    // which we get from this.validationRegex
    regexValidator(propVal, regexKey) {
      if (typeof propVal === 'undefined') {
        return false;
      }
      if (propVal === '') {
        return true;
      }
      return !this.validationRegex[regexKey].test(propVal);
    },
    // checks whether or not a field is required based on banking info requirements
    // sent over from currency cloud
    currencyCloudDependantRequirements(propName) {
      if (propName === 'bicSwift') {
        if (this.requiredVendorFields.includes('sortCode')) {
          return false;
        }
        if (this.requiredVendorFields.includes('aba')) {
          return !this.selectedVendor.wireRoutingNumber;
        }
        return this.requiredVendorFields.includes('bicSwift');
      }
      // temporary workaround to be re-visited on local rails for Currency Cloud
      // if (propName === 'aba') {
      //   return (
      //     this.requiredVendorFields.includes('aba') &&
      //     !this.selectedVendor.swiftCode
      //   );
      // }
      if (propName === 'iban') {
        return this.requiredVendorFields.includes('sortCode')
          ? false
          : this.requiredVendorFields.includes('iban');
      }
      return true;
    },
    handleNext(event) {
      if (!event) return;

      const inputs = [
        this.$refs.vendorBankNameInput,
        this.$refs.vendorNameOnAccountInput,
        this.$refs.vendorAccountNumberInput,
        this.$refs.vendorSwiftCodeInput,
        this.$refs.vendorIbanInput,
        this.$refs.vendorSortCodeInput,
        this.$refs.vendorRoutingNumberInput,
        this.$refs.vendorWireRoutingNumberInput,
        this.$refs.vendorBsbCodeInput,
        this.$refs.vendorBankCodeInput,
        this.$refs.vendorBranchCodeInput,
        this.$refs.vendorClabeInput,
        this.$refs.vendorCnapsInput,
        this.$refs.vendorIfscInput,
        this.$refs.vendorLine1Input,
        this.$refs.vendorLine2Input,
        this.$refs.vendorStateInput,
        this.$refs.vendorCityInput,
        this.$refs.vendorPostalCodeInput,
      ].filter((input) => input);
      inputs.forEach((field) => field.$touch());
      const isValid = inputs.every((field) => field.isValid);
      if (isValid) {
        const { line1, line2, state, city, postalCode, ...rest } = this.bank;
        const bankData = {
          ...rest,
          bankAddress: {
            line1,
            line2,
            state,
            city,
            postalCode,
          },
        };
        this.$emit('next-tab', bankData);
      }
    },
    handlePrev(event) {
      if (event) {
        this.$emit('prev-tab');
      }
    },
  },
};
</script>
<style lang="less" module>
.modalContext {
  color: black;
  padding-bottom: 30px;
  text-align: center;
  font-weight: bold;
}
</style>
